import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { Button } from "wiz-front-shared/components/button/Button";
import { CheckBox } from "wiz-front-shared/components/checkBox/CheckBox";
import { withdrawDataAtom, pushWithdrawStepAtom } from "store/withdrawStore";
import { userStore } from "store/userStore";
import { requestApp, WEB_TO_APP } from "common/webview";
import './Withdraw.css';

export const WithdrawStep3 = () => {
    const { t } = useTranslation();
    const [data, setData] = useAtom(withdrawDataAtom);
    const pushStep = useSetAtom(pushWithdrawStepAtom);
    const withdrawState = useAtomValue(userStore.withdraw.stateAtom);

    const [checked, setChecked] = useState<boolean>(data.finalConfirmed);
    const [submitting, setSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string>("");

    const buildReasonPayload = (): string => {
        // "기타" → 사용자가 직접 입력한 의견 텍스트
        // 그 외 → 화면에 표시된 라벨 텍스트(i18n) 그대로 백엔드 전송
        // (운영 분석용 tb_user_log.err_msg 에 의미 있는 문구로 적재됨)
        if (data.reason === "other") return data.feedback;
        if (!data.reason) return "";
        return t(`withdraw.step2.reasons.${data.reason}`);
    };

    const handleConfirm = () => {
        // submitting(로컬) 가드는 컴포넌트 재마운트로 풀리므로, 전역 isPending 도 함께 확인 — 중복 탈퇴 호출 방지.
        if (!checked || submitting || withdrawState.isPending) return;
        setData(prev => ({ ...prev, finalConfirmed: true }));
        setErrorMsg("");
        // 이전 탈퇴 시도의 잔존 상태(ready/error)를 먼저 리셋 — 안 하면 submitting=true 로
        //   바뀌는 순간 stale 응답으로 useEffect 가 즉시 오발화.
        userStore.withdraw.onDefault();
        setSubmitting(true);
        const reason = buildReasonPayload();
        userStore.onWithdraw(reason ? { reason } : undefined);
    };

    // API 응답 처리
    useEffect(() => {
        if (!submitting) return;

        if (withdrawState.status === "ready") {
            setSubmitting(false);
            userStore.withdraw.onDefault();
            // API 성공 즉시 native 에 cleanup 신호 — pushStep(4) 직후 사용자가 device back 으로
            // webview 를 닫아 WithdrawDone 의 WITHDRAW_DONE 메시지를 못 보내는 race 방지.
            // (이미 백엔드는 deactivate 했으므로 MMKV/소셜 정리는 동기 진행)
            requestApp(WEB_TO_APP.WITHDRAW_CLEANUP);
            pushStep(4);
        } else if (withdrawState.status === "error") {
            setSubmitting(false);
            const msg = withdrawState.error?.message || withdrawState.data?.message;
            setErrorMsg(msg || t("withdraw.step3.errorDefault"));
            userStore.withdraw.onDefault();
        }
    }, [withdrawState.status, withdrawState.data, withdrawState.error, submitting, pushStep, t]);

    return (
        <div className="withdraw-step">
            <span className="title">{t("withdraw.step3.title")}</span>
            <span className="info">{t("withdraw.step3.info")}</span>

            <div className="withdraw-warning-box">
                <span className="withdraw-warning-title">{t("withdraw.step3.noticeTitle")}</span>
                <span>{t("withdraw.step3.notice")}</span>
            </div>

            <div className="withdraw-final-confirm">
                <CheckBox
                    id="withdraw-final-confirm"
                    title={t("withdraw.step3.confirmLabel")}
                    checked={checked}
                    setChecked={setChecked}
                />
            </div>

            {errorMsg && (
                <div style={{ marginTop: 12, fontSize: 13, color: "var(--warning)" }}>{errorMsg}</div>
            )}

            <div className="withdraw-bottom-btn">
                <Button
                    text={submitting ? t("withdraw.loading") : t("withdraw.btnConfirm")}
                    onPress={handleConfirm}
                    buttonColor="var(--warning)"
                    disabled={!checked || submitting || withdrawState.isPending}
                />
            </div>
        </div>
    );
};
